import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';

import {
  archiveAccount,
  formatAccountLabel,
  unarchiveAccount,
  type Account,
} from '@/features/accounts/accounts.api';
import { accountsQueryKey } from '@/features/accounts/useAccounts';
import { useUIStore } from '@/store/ui';

/** The hub's `detail` verbatim (409 "account is connected; …", 404), else `fallback`. */
function errorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === 'string' && detail.length > 0) {
      return detail;
    }
  }
  return fallback;
}

/**
 * Mutation over `POST /api/v1/accounts/{id}/archive` — hide an account from
 * the pool (admin only; DB-only on the hub, the gateway is never touched).
 *
 * Not optimistic, like `useRescanAccount`: the row leaves the pool once the
 * hub confirms, by writing the returned row (now carrying `archived_at`)
 * into the `['accounts']` cache in place. The hub publishes no realtime event
 * for it; other consoles pick it up with their next 30s accounts poll. When
 * the hidden account was the one the inbox is scoped to, the scope falls back
 * to 「所有账号」 (the pool no longer offers it).
 */
export function useArchiveAccount(): UseMutationResult<Account, unknown, string> {
  const queryClient = useQueryClient();

  return useMutation<Account, unknown, string>({
    mutationFn: (accountId) => archiveAccount(accountId),

    onSuccess: (updated) => {
      queryClient.setQueryData<Account[]>(accountsQueryKey, (rows) =>
        rows?.map((row) => (row.id === updated.id ? updated : row)),
      );
      const ui = useUIStore.getState();
      if (ui.selectedAccountId === updated.id) ui.setSelectedAccountId(null);
      toast.success(`已从账号池隐藏 ${formatAccountLabel(updated)}`);
    },

    onError: (error) => {
      toast.error(errorMessage(error, '隐藏失败，请重试'));
    },
  });
}

/** Mutation over `POST /api/v1/accounts/{id}/unarchive` — the 「恢复」 action. */
export function useUnarchiveAccount(): UseMutationResult<Account, unknown, string> {
  const queryClient = useQueryClient();

  return useMutation<Account, unknown, string>({
    mutationFn: (accountId) => unarchiveAccount(accountId),

    onSuccess: (updated) => {
      queryClient.setQueryData<Account[]>(accountsQueryKey, (rows) =>
        rows?.map((row) => (row.id === updated.id ? updated : row)),
      );
      toast.success(`已恢复 ${formatAccountLabel(updated)} 到账号池`);
    },

    onError: (error) => {
      toast.error(errorMessage(error, '恢复失败，请重试'));
    },
  });
}
